import * as React from "react";

import { locKeys } from "../l10";
import { observer } from "mobx-react";

import { UitkPrimaryButton } from "@egds/react-core/button";
import { UitkCard, UitkCardContentSection, UitkCardLink } from "@egds/react-core/cards";
import { UitkLayoutFlex } from "@egds/react-core/layout-flex";
import { UitkLayoutPosition } from "@egds/react-core/layout-position";
import { UitkSpacing, UitkSpacingHr } from "@egds/react-core/spacing";
import { UitkText } from "@egds/react-core/text";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { useLocalization } from "@shared-ui/localization-context";

import type { FlightCardWithButtonContentProps } from "components/flexComponents/Flights/typings";
import { A11yMessageParams, getA11yMessage, getElapsedTime, TripType } from "components/flexComponents/Flights/util";
import { TrackedLink } from "components/utility/analytics/TrackedLink";

import { FlightLeg } from "./FlightLeg";

export const FlightCardWithButtonContent: React.FC<FlightCardWithButtonContentProps> = observer((props) => {
  const { flight, index, isMobile, showCityName } = props;

  const track = useClickTracker();
  const { formatText } = useLocalization();

  const airCarrierEBF = "Bargain Flight";
  const airCarrier = flight.airCarrier?.name ?? airCarrierEBF;
  const isOneWay = flight.tripType === TripType.ONE_WAY;
  const formattedElapsedTime = getElapsedTime(flight.elapsedHours, Math.floor(flight.elapsedHours / 24));
  const a11yMessageParams: A11yMessageParams = { airCarrier, isOneWay, flight, formattedElapsedTime, showCityName };

  return (
    <UitkCard border data-testid={`flightListingWithButtonCard-${index}`}>
      <UitkCardLink>
        <TrackedLink
          moduleName="Flights"
          href={flight.searchUrl}
          rfrr={`flightListingWithButtonCard-${index}`}
          linkName="Flight listing module card has been clicked"
          data-testid={`flightListingWithButton-CardLink-${index}`}
        >
          {getA11yMessage(a11yMessageParams, formatText)}
        </TrackedLink>
      </UitkCardLink>

      <UitkSpacing padding={{ blockstart: "six", blockend: "four", inline: isMobile ? "four" : "six" }}>
        <UitkCardContentSection padded={false}>
          <UitkLayoutFlex direction="column">
            <FlightLeg
              airCarrier={airCarrier}
              airCarrierEBF={airCarrierEBF}
              flight={flight}
              isMobile={isMobile}
              isOneWay={isOneWay}
              isOutBound
              showCityName={showCityName}
            />

            {!isOneWay && (
              <FlightLeg
                airCarrier={airCarrier}
                airCarrierEBF={airCarrierEBF}
                flight={flight}
                isMobile={isMobile}
                isOneWay={isOneWay}
                isOutBound={false}
                showCityName={showCityName}
              />
            )}
          </UitkLayoutFlex>

          <UitkSpacingHr />

          <UitkLayoutFlex
            alignItems={isMobile ? "end" : "center"}
            direction={isMobile ? "column-reverse" : undefined}
            justifyContent="space-between"
          >
            <UitkSpacing margin={isMobile ? { blockstart: "one", inlineend: "four" } : undefined}>
              <UitkText size={isMobile ? 100 : 200} theme="secondary" weight="regular">
                {formattedElapsedTime}
              </UitkText>
            </UitkSpacing>

            <UitkLayoutPosition position={{ zindex: "layer3" }}>
              <UitkPrimaryButton
                tag="a"
                href={flight.searchUrl}
                data-testid={`flightListingWithButton-CTA-${index}`}
                onClick={() =>
                  track(
                    `Flights.flightListingWithButtonCTA-${index}.click`,
                    "Flight listing module CTA has been clicked"
                  )
                }
              >
                {formatText(locKeys.flightFindDealsFrom, flight.formattedTicketPrice)}
              </UitkPrimaryButton>
            </UitkLayoutPosition>
          </UitkLayoutFlex>
        </UitkCardContentSection>
      </UitkSpacing>
    </UitkCard>
  );
});
